import { useState } from 'react'
import { ChevronDown, Target, TrendingUp, TrendingDown, Minus, FileText, Lock, ArrowDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { calculateProgress } from '@/utils/kpiProgress'
import type { MeasurementDirection, MeasurementMode } from '@/types/kpi'

// Infer measurement mode from existing KPI data (for backward compatibility)
const inferMeasurementMode = (kpi: { unit?: string; measurement_mode?: MeasurementMode }): MeasurementMode => {
  if (kpi.measurement_mode) return kpi.measurement_mode
  
  const unit = (kpi.unit || '').toLowerCase().trim()
  
  if (unit === '%' || unit === 'percent' || unit === 'percentage') {
    return 'percentage'
  }
  if (unit === 'status' || unit === 'rag' || unit === 'rating') {
    return 'rating'
  }
  if (unit === 'achieved' || unit === 'yes/no' || unit === 'complete') {
    return 'binary'
  }
  
  return 'numeric'
}

// Infer measurement direction from existing KPI data
const inferMeasurementDirection = (kpi: { unit?: string; name?: string; measurement_direction?: MeasurementDirection }): MeasurementDirection => {
  if (kpi.measurement_direction) return kpi.measurement_direction
  
  const unit = (kpi.unit || '').toLowerCase()
  const name = (kpi.name || '').toLowerCase()
  
  if (unit.includes('day') || unit.includes('hour') || unit.includes('minute') ||
      name.includes('turnaround') || name.includes('response time') || 
      name.includes('waiting') || name.includes('defect') || name.includes('error')) {
    return 'lower_is_better'
  }
  
  return 'higher_is_better'
}

// Circle Progress Component with Icon
interface CircleProgressProps {
  value: number
  maxValue: number
  size?: number
  strokeWidth?: number
  color?: string
  icon?: React.ReactNode
  showIcon?: boolean
}

const CircleProgress = ({
  value,
  maxValue,
  size = 40,
  strokeWidth = 3,
  color = '#003B5C',
  icon,
  showIcon = false
}: CircleProgressProps) => {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const fillPercentage = Math.min(value / maxValue, 1)
  const strokeDashoffset = circumference * (1 - fillPercentage)
  const iconSize = size * 0.4

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className="fill-transparent stroke-gray-200"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className="fill-transparent transition-all duration-300"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          strokeLinecap="round"
        />
      </svg>
      {showIcon && icon && (
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ color }}
        >
          <div style={{ width: iconSize, height: iconSize }}>
            {icon}
          </div>
        </div>
      )}
    </div>
  )
}

// Progress-ring colour by completion %. This is intentionally KEPT after the
// RAG-status removal: the ring shows intrinsic progress (a number), and the
// red/amber/green arc is a quick legibility cue on that number — distinct from
// the "At Risk / Off Track" status badges/labels that were removed. Detailed
// view only.
const progressRingColor = (pct: number): string => {
  if (pct >= 70) return '#10b981' // green  — on/ahead
  if (pct >= 40) return '#f59e0b' // amber  — partway
  return '#ef4444'                // red    — early
}

// Glyph shown inside the progress ring. Progress-driven (same thresholds as
// the colour), NOT the removed RAG status — it inherits the ring colour and is
// a glance cue on pace (↗ ahead / – steady / ↘ early). No status text.
const progressTrendIcon = (pct: number): React.ReactNode => {
  if (pct >= 70) return <TrendingUp className="w-full h-full" />
  if (pct >= 40) return <Minus className="w-full h-full" />
  return <TrendingDown className="w-full h-full" />
}

// Types matching your existing data structure
interface KPI {
  id: string
  name: string
  description?: string
  target_value: number
  current_value: number
  unit: string
  status: string
  trend?: string
  // Enterprise measurement configuration
  measurement_direction?: MeasurementDirection
  measurement_mode?: MeasurementMode
}

interface Objective {
  id: number
  title: string
  description?: string | null
  progress: number
  status: string
  owner: string
  functional_area: string
  kpis?: KPI[]
}

interface ObjectivesKPIsViewProps {
  objectives: Objective[]
  evidence?: Array<{ kpiId?: string; [key: string]: any }>
  userRole?: 'employee' | 'manager'
  /**
   * Simple ↔ Detailed view. Simple suppresses progress %s, RAG status badges,
   * KPI target/current readouts, and progress bars so the surface reads as a
   * journal of objectives + KPIs rather than a metrics dashboard. Detailed
   * restores the full enterprise view.
   */
  journeyViewMode?: 'simple' | 'detailed'
  onAddKPIEvidence?: (kpiId: string, kpiName: string, objectiveId: number) => void
  reviewPeriod?: 'MIDYEAR' | 'YEAREND'
}

export default function ObjectivesKPIsView({
  objectives = [],
  evidence = [],
  journeyViewMode = 'simple',
  onAddKPIEvidence,
  reviewPeriod,
}: ObjectivesKPIsViewProps) {
  const isSimple = journeyViewMode === 'simple'
  // Filter out rejected objectives
  const activeObjectives = objectives.filter(obj => obj.status !== 'rejected')
  
  const [expandedObjectives, setExpandedObjectives] = useState<Set<number>>(
    new Set(activeObjectives.length > 0 ? [activeObjectives[0].id] : [])
  )

  // Calculate dynamic objective progress based on KPIs using enterprise calculation
  // Uses period-specific KPI values when reviewPeriod is specified
  const getObjectiveProgress = (objective: Objective) => {
    if (!objective.kpis || objective.kpis.length === 0) {
      return objective.progress || 0
    }
    
    const totalProgress = objective.kpis.reduce((sum, kpi) => {
      const mode = inferMeasurementMode(kpi)
      const direction = inferMeasurementDirection(kpi)
      
      // Use period-specific KPI value if reviewPeriod is specified
      const kpiAny = kpi as any
      const periodCurrentValue = reviewPeriod === 'MIDYEAR'
        ? (kpiAny.midyear_value ?? kpi.current_value)
        : reviewPeriod === 'YEAREND'
          ? (kpiAny.yearend_value ?? kpi.current_value)
          : kpi.current_value
      
      const progressResult = calculateProgress(
        periodCurrentValue,
        kpi.target_value,
        direction,
        mode
      )
      return sum + progressResult.progress
    }, 0)
    
    return Math.round(totalProgress / objective.kpis.length)
  }

  const toggleObjective = (id: number) => {
    setExpandedObjectives((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const getTrendIcon = (trend?: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-600" />
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-600" />
      case 'stable':
        return <Minus className="w-4 h-4 text-gray-600" />
      default:
        return null
    }
  }

  if (activeObjectives.length === 0) {
    if (isSimple) {
      return (
        <div className="max-w-[720px] mx-auto py-12 text-center">
          <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase mb-3">
            Nothing yet
          </p>
          <p className="font-editorial italic text-quarterly-ink-muted text-[18px] leading-[1.55]">
            Active objectives will appear here once your people leader assigns them.
          </p>
        </div>
      )
    }
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Target className="w-16 h-16 text-gray-300 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Objectives</h3>
        <p className="text-sm text-gray-500">Active objectives will appear here once they are assigned</p>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────
  // Simple-mode Quarterly editorial layout. Each objective is a chapter
  // section: roman numeral mark → Larken serif title → italic Newsreader
  // description → numbered list of KPIs. No cards, no white boxes, no
  // RAG colours — the surrounding bone surface from MemberKPAPanel.tsx
  // shows through. Click "Continue reading" to toggle expansion.
  // ─────────────────────────────────────────────────────────────────────
  if (isSimple) {
    return (
      <div className="max-w-[720px] mx-auto">
        <ol className="divide-y divide-quarterly-rule">
          {activeObjectives.map((objective, objIdx) => {
            const isPendingApproval = objective.status === 'pending_approval' || objective.status === 'pending-approval'
            const isExpanded = expandedObjectives.has(objective.id)
            return (
              <li key={objective.id} className="py-8 first:pt-0">
                {isPendingApproval && (
                  <p className="text-[11px] font-medium tracking-[0.22em] uppercase text-quarterly-tobacco mb-3 flex items-center gap-1.5">
                    <Lock className="h-3 w-3" />
                    Awaiting your people leader's approval — read-only
                  </p>
                )}

                {/* Objective mark — small caps eyebrow above the objective title */}
                <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase mb-2">
                  Objective {objIdx + 1}
                </p>

                {/* Objective title — Larken serif */}
                <h3 className="font-serif text-[26px] md:text-[30px] leading-[1.15] tracking-tight text-quarterly-ink mb-3 text-balance">
                  {objective.title}
                </h3>

                {/* Description — italic Newsreader. Always shown in Simple,
                    no gating; the description IS the conversation starter. */}
                {objective.description && (
                  <p className="font-editorial italic text-quarterly-ink-muted text-[17px] leading-[1.55] mb-5 max-w-[600px]">
                    {objective.description}
                  </p>
                )}


                {/* KPIs — numbered list with roman numerals. Click "Read more"
                    on each KPI to open the Impact Portfolio for that KPI. */}
                {objective.kpis && objective.kpis.length > 0 ? (
                  <div className="border-l border-quarterly-rule pl-6">
                    <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase mb-4">
                      How we'll know
                    </p>
                    <ol className="space-y-5">
                      {objective.kpis.map((kpi, kpiIdx) => {
                        const kpiEvidenceCount = evidence.filter((e) => e.kpiId === kpi.id).length
                        const numeral = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x']
                        const mark = numeral[kpiIdx] || String(kpiIdx + 1)
                        return (
                          <li key={kpi.id} className="grid grid-cols-[2.5rem_1fr] gap-3 items-baseline">
                            <span className="font-serif italic text-quarterly-ink-muted text-[17px] tabular-nums">
                              {mark}.
                            </span>
                            <div>
                              <p className="font-serif text-[18px] text-quarterly-ink leading-snug mb-1">
                                {kpi.name}
                              </p>
                              {kpi.description && (
                                <p className="font-editorial italic text-quarterly-ink-muted text-[15px] leading-[1.55] mb-2">
                                  {kpi.description}
                                </p>
                              )}
                              {onAddKPIEvidence && (
                                <button
                                  onClick={() => !isPendingApproval && onAddKPIEvidence(kpi.id, kpi.name, objective.id)}
                                  disabled={isPendingApproval}
                                  className={cn(
                                    'text-[11px] font-medium tracking-[0.18em] uppercase transition-colors',
                                    isPendingApproval
                                      ? 'text-quarterly-ink-muted/50 cursor-not-allowed'
                                      : 'text-quarterly-tobacco hover:underline'
                                  )}
                                >
                                  {isPendingApproval ? (
                                    <>Read-only</>
                                  ) : (
                                    <>
                                      View entries
                                      {kpiEvidenceCount > 0 && (
                                        <span className="ml-1 not-italic">({kpiEvidenceCount})</span>
                                      )}
                                      <span className="ml-1.5">→</span>
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </li>
                        )
                      })}
                    </ol>
                  </div>
                ) : (
                  <p className="font-editorial italic text-quarterly-ink-muted text-[15px]">
                    No KPIs linked yet.
                  </p>
                )}

                {/* Suppress unused expansion state in Simple — toggle isn't used here */}
                <span className="hidden">{isExpanded ? '' : ''}</span>
              </li>
            )
          })}
        </ol>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="space-y-3">
        {activeObjectives.map((objective) => {
          const isPendingApproval = objective.status === 'pending_approval' || objective.status === 'pending-approval'
          const isExpanded = expandedObjectives.has(objective.id)
          const evidenceCount = objective.kpis?.reduce(
            (sum, kpi) => sum + evidence.filter((e) => e.kpiId === kpi.id).length,
            0
          ) || 0
          
          // Calculate dynamic progress
          const dynamicProgress = getObjectiveProgress(objective)

          return (
            <div
              key={objective.id}
              className={cn(
                "border rounded-xl bg-white overflow-hidden transition-all",
                isPendingApproval ? "border-amber-300 bg-amber-50/30" : "border-gray-200 hover:shadow-lg"
              )}
            >
              {isPendingApproval && (
                <div className="bg-amber-100 border-b border-amber-200 px-6 py-2 flex items-center gap-2 text-amber-800">
                  <Lock className="h-4 w-4" />
                  <span className="text-sm font-medium">This objective is pending approval and cannot be edited</span>
                </div>
              )}
              <button
                onClick={() => toggleObjective(objective.id)}
                className={cn(
                  "w-full px-6 py-4 flex items-center justify-between transition-colors",
                  !isPendingApproval && "hover:bg-gray-50"
                )}
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 text-heritage-blue">
                    <Target className="w-6 h-6" />
                  </div>

                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-base font-semibold text-gray-900">
                        {objective.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <span className="font-medium">{objective.owner}</span>
                      </span>
                      <span>•</span>
                      <span>{objective.functional_area}</span>
                      {objective.kpis && objective.kpis.length > 0 && (
                        <>
                          <span>•</span>
                          <span>{objective.kpis.length} KPIs</span>
                        </>
                      )}
                      {evidenceCount > 0 && (
                        <>
                          <span>•</span>
                          <span className="text-heritage-blue font-medium">
                            {evidenceCount} Impact Portfolio
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Right-side progress block — Detailed only. Simple keeps
                      the row visually quiet so completion isn't the implied
                      verdict on the conversation. */}
                  {!isSimple && (
                    <div className="flex items-center gap-3">
                      <div className="text-right mr-2">
                        <div className="text-2xl font-semibold text-gray-900">
                          {dynamicProgress}%
                        </div>
                        <div className="text-xs text-gray-500">Complete</div>
                      </div>
                      <CircleProgress
                        value={dynamicProgress}
                        maxValue={100}
                        size={56}
                        strokeWidth={4}
                        color={progressRingColor(dynamicProgress)}
                        icon={progressTrendIcon(dynamicProgress)}
                        showIcon={true}
                      />
                    </div>
                  )}
                </div>

                <ChevronDown
                  className={cn(
                    'w-5 h-5 text-gray-400 ml-4 transition-transform',
                    isExpanded && 'rotate-180'
                  )}
                />
              </button>

              {isExpanded && (
                <div className="px-6 pb-6 pt-2 border-t border-gray-100 bg-gray-50/50">
                  {objective.description && (
                    <p className="text-sm text-gray-600 mb-4 mt-2">
                      {objective.description}
                    </p>
                  )}

                  {objective.kpis && objective.kpis.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {objective.kpis.map((kpi) => {
                        // Infer measurement config from existing data (for backward compatibility)
                        const mode = inferMeasurementMode(kpi)
                        const direction = inferMeasurementDirection(kpi)
                        
                        // Use period-specific KPI value if reviewPeriod is specified
                        // This ensures mid-year view shows mid-year value, not the overall current value
                        const kpiAny = kpi as any
                        const periodCurrentValue = reviewPeriod === 'MIDYEAR'
                          ? (kpiAny.midyear_value ?? kpi.current_value)
                          : reviewPeriod === 'YEAREND'
                            ? (kpiAny.yearend_value ?? kpi.current_value)
                            : kpi.current_value
                        
                        // Use enterprise progress calculation with period-specific value
                        const progressResult = calculateProgress(
                          periodCurrentValue,
                          kpi.target_value,
                          direction,
                          mode
                        )
                        const kpiProgress = progressResult.progress
                        const kpiEvidenceCount = evidence.filter((e) => e.kpiId === kpi.id).length

                        // Determine display format based on measurement mode
                        const isRating = mode === 'rating'
                        const isBinary = mode === 'binary'
                        const isLowerBetter = direction === 'lower_is_better'

                        return (
                          <div
                            key={kpi.id}
                            className="p-4 rounded-lg border border-gray-200 bg-white hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium text-gray-900">
                                    {kpi.name}
                                  </h4>
                                  {getTrendIcon(kpi.trend)}
                                  {isLowerBetter && !isRating && !isBinary && (
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-blue-50 text-blue-700 border-blue-200">
                                      <ArrowDown className="h-2.5 w-2.5 mr-0.5" />
                                      Lower is better
                                    </Badge>
                                  )}
                                </div>
                                {kpi.description && (
                                  <p className="text-xs text-gray-500 mb-2">{kpi.description}</p>
                                )}
                                {!isSimple && (
                                  <div className="flex items-baseline gap-2">
                                    {isRating ? (
                                      // Rating display — neutral progress readout
                                      <span className="text-2xl font-semibold text-heritage-blue">
                                        {Math.round(kpiProgress)}%
                                      </span>
                                    ) : isBinary ? (
                                      // Binary display (Achieved / Not Achieved) - uses period-specific value
                                      <span className={cn(
                                        "text-lg font-semibold px-2 py-0.5 rounded",
                                        (periodCurrentValue || 0) >= 100 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                                      )}>
                                        {(periodCurrentValue || 0) >= 100 ? 'Achieved' : 'Not Achieved'}
                                      </span>
                                    ) : (
                                      // Numeric/Percentage display - uses period-specific value
                                      <>
                                        <span className="text-2xl font-semibold text-heritage-blue">
                                          {kpi.unit === '$'
                                            ? `$${periodCurrentValue.toLocaleString()}`
                                            : periodCurrentValue}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                          /{' '}
                                          {kpi.unit === '$'
                                            ? `$${kpi.target_value.toLocaleString()}`
                                            : kpi.target_value}{' '}
                                          {kpi.unit !== '$' && kpi.unit}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                              {/* Right-rail progress — Detailed only */}
                              {!isSimple && (
                                <div className="flex flex-col items-end gap-2">
                                  <CircleProgress
                                    value={kpiProgress}
                                    maxValue={100}
                                    size={48}
                                    strokeWidth={4}
                                    color={progressRingColor(kpiProgress)}
                                    icon={progressTrendIcon(kpiProgress)}
                                    showIcon={true}
                                  />
                                </div>
                              )}
                            </div>

                            {/* Bottom progress bar — Detailed only */}
                            {!isSimple && (
                              <div className="mb-3">
                                <div className="flex justify-between text-xs text-gray-500 mb-1">
                                  <span>Progress</span>
                                  <span>{Math.round(kpiProgress)}%</span>
                                </div>
                                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full transition-all rounded-full bg-heritage-blue"
                                    style={{
                                      width: `${Math.min(kpiProgress, 100)}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {onAddKPIEvidence && (
                              <button
                                onClick={() => !isPendingApproval && onAddKPIEvidence(kpi.id, kpi.name, objective.id)}
                                disabled={isPendingApproval}
                                className={cn(
                                  "w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2",
                                  isPendingApproval
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "text-heritage-blue bg-blue-50 hover:bg-blue-100"
                                )}
                              >
                                {isPendingApproval ? <Lock className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                                {isPendingApproval ? "Read-Only" : "Impact Portfolio"}
                                {!isPendingApproval && kpiEvidenceCount > 0 && (
                                  <Badge className="ml-1 h-5 min-w-[20px] px-1.5 text-[10px] bg-heritage-blue text-white border-0">
                                    {kpiEvidenceCount}
                                  </Badge>
                                )}
                              </button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No KPIs linked to this objective yet
                    </p>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
